import dayjs from 'dayjs'
import { Check, ChevronRight } from 'lucide-react'
import { Box, Button, Link, Stack, Typography } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { Notice, ProgressBar, StatusPill, StepCard, StepTitle } from '../../components/ui'
import {
  ENTITY_SECTIONS,
  ENTITY_SECTION_LABELS,
  entityPercent,
  entitySectionProgress,
  entityStatus,
} from '../../lib/completion'
import { clientLocation, entityLocation } from '../flow'
import { MISC_PRIMARY } from '../../theme/onboardingTheme'

/**
 * Entity dashboard (MISC Onboarding Sync): the client picks which entity to work on, sees percent complete per
 * entity, and returns here after finishing each one — so entities can be delegated and tracked.
 */
export function StepEntityDashboard() {
  const { state, goTo, canReach } = useOnboarding()
  const completeCount = state.entities.filter((e) => entityPercent(e) >= 100).length

  return (
    <StepCard>
      <StepTitle
        subtitle={
          <>
            Complete the DRLP, Individual Licensees, and State Login Credentials sections for each business entity.
            Different people can work on different entities — progress is tracked per entity.
          </>
        }
      >
        Which entity would you like to work on?
      </StepTitle>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>
          {completeCount} of {state.entities.length} entities complete
        </Typography>
        <Link component="button" type="button" onClick={() => goTo(clientLocation('entitySetup'))} sx={{ fontSize: 14, fontWeight: 600 }}>
          Edit entity list
        </Link>
      </Box>

      {state.entities.length === 0 ? (
        <Notice tone="warning">No business entities have been added yet. Go back to Business Entity Setup to add one.</Notice>
      ) : null}

      <Stack spacing={2}>
        {state.entities.map((entity) => {
          const pct = entityPercent(entity)
          const status = entityStatus(entity)
          const firstIncomplete =
            ENTITY_SECTIONS.find((s) => entitySectionProgress(entity, s).score < 1) ?? 'states'
          return (
            <Box
              key={entity.id}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: { xs: 2, sm: 2.5 },
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.2fr) minmax(0, 1.5fr) auto' },
                alignItems: 'center',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#0f172a' }}>
                    {entity.name || 'Unnamed entity'}
                  </Typography>
                  <StatusPill status={status} />
                </Box>
                <Typography sx={{ mt: 0.25, fontSize: 12, color: '#64748b' }}>
                  NPN {entity.npn || '—'} · {entity.statesToHandle.length} state{entity.statesToHandle.length === 1 ? '' : 's'} ·{' '}
                  {entity.updatedAt ? `Last updated ${dayjs(entity.updatedAt).format('MMM D, h:mm A')}` : 'Not started'}
                </Typography>
                <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <ProgressBar value={pct} />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: pct >= 100 ? '#065f46' : '#0f172a', minWidth: 44, textAlign: 'right' }}>
                    {pct}%
                  </Typography>
                </Box>
              </Box>

              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5, gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
                {ENTITY_SECTIONS.map((section) => {
                  const score = entitySectionProgress(entity, section).score
                  const done = score >= 1
                  const reachable = canReach(entityLocation(entity.id, section))
                  return (
                    <li key={section}>
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => goTo(entityLocation(entity.id, section))}
                        disabled={!reachable}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          fontSize: 13,
                          color: done ? '#065f46' : '#334155',
                          textAlign: 'left',
                          '&:disabled': { cursor: 'default', textDecoration: 'none', color: '#94a3b8' },
                        }}
                      >
                        <Box
                          component="span"
                          aria-hidden
                          sx={{
                            display: 'grid',
                            placeItems: 'center',
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            flexShrink: 0,
                            bgcolor: done ? '#059669' : '#e2e8f0',
                            color: '#fff',
                          }}
                        >
                          {done ? <Check size={11} strokeWidth={3} /> : null}
                        </Box>
                        {ENTITY_SECTION_LABELS[section]}
                        {!done && score > 0 ? (
                          <Box component="span" sx={{ color: '#94a3b8' }}>
                            ({Math.round(score * 100)}%)
                          </Box>
                        ) : null}
                      </Link>
                    </li>
                  )
                })}
              </Box>

              <Button
                type="button"
                variant={status === 'Complete' ? 'outlined' : 'contained'}
                endIcon={<ChevronRight size={16} />}
                onClick={() => goTo(entityLocation(entity.id, status === 'Complete' ? 'states' : firstIncomplete))}
                sx={{ justifySelf: { xs: 'start', xl: 'end' }, whiteSpace: 'nowrap', ...(status === 'Complete' ? { borderColor: MISC_PRIMARY } : {}) }}
              >
                {status === 'Not started' ? 'Start' : status === 'Complete' ? 'Review' : 'Continue'}
              </Button>
            </Box>
          )
        })}
      </Stack>

      <Typography sx={{ mt: 3, fontSize: 13, color: '#64748b' }}>
        When every entity is complete, select Continue to move on to contacts, attestation, and the test Open Tax Report.
      </Typography>
    </StepCard>
  )
}
